/**
 * Transitions — redelivery projects.
 * Routes: #/transitions, #/transitions/:msn, #/transitions/:msn/documents
 * MSN 5034 dashboard is the 22-Sep-26 control sheet, fed by the project record.
 */

import { MSN5034 as P } from "./transitions-msn5034-brain.mjs";
import { LIFE_SVG, TIMELINE_SVG } from "./transitions-msn5034-charts.mjs";

export const TRANSITIONS_NAV = {
  group: "transitions",
  label: "Transitions",
  adminOnly: true,
  defaultOpen: true,
  children: [{ key: "transitions", label: "All transitions" }],
};

export function isTransitionsRoute(routeKey) {
  const key = String(routeKey || "").trim().replace(/\/+$/, "");
  return key === "transitions" || key.startsWith("transitions/");
}

const STYLE_ID = "transitions-styles-v2";

function ensureStyles() {
  const prev = document.getElementById("transitions-styles-v1");
  if (prev) prev.remove();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.tr-wrap { color:#1A1A1A; font-size:13px; line-height:1.35; background:#fff; padding:8px 4px 28px; }
.tr-head { border-bottom:4px solid #C00000; padding-bottom:10px; margin-bottom:14px; display:flex; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.tr-head h1 { font-size:22px; margin:0 0 4px; font-weight:700; }
.tr-sub, .tr-note, .tr-small { color:#5F5F5F; font-size:12px; }
.tr-stamp { text-align:right; font-size:12px; color:#5F5F5F; }
.tr-stamp b { color:#1A1A1A; font-size:14px; }
.tr-status { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:10px; margin:0 0 16px; }
.tr-status div { background:#F2F2F2; padding:10px 12px; border-left:4px solid #C00000; }
.tr-status span { display:block; font-size:11px; color:#5F5F5F; margin-bottom:2px; }
.tr-status b { font-size:14px; }
.tr-h { font-size:17px; margin:26px 0 8px; padding-bottom:4px; border-bottom:1px solid #BFBFBF; font-weight:700; }
.tr-h small { font-weight:400; color:#5F5F5F; font-size:12px; margin-left:8px; }
.tr-rule { background:#F2F2F2; padding:8px 12px; margin:8px 0; border-left:4px solid #C00000; }
.tr-tabs { display:flex; gap:0; margin:0 0 10px; }
.tr-tabs a { padding:6px 10px; color:#374151; text-decoration:none; border-bottom:2px solid transparent; }
.tr-tabs a.on { color:#C00000; border-bottom-color:#C00000; font-weight:700; }
.tr-table { border-collapse:collapse; width:100%; margin:8px 0 6px; font-size:12.5px; background:#fff; }
.tr-table th { background:#C00000; color:#fff; text-align:left; padding:6px 8px; font-weight:700; vertical-align:bottom; }
.tr-table td { border-bottom:1px solid #BFBFBF; padding:6px 8px; vertical-align:top; }
.tr-table td.num, .tr-table th.num { text-align:right; white-space:nowrap; }
.tr-table td.ref { white-space:nowrap; font-weight:700; }
.tr-table tr.total td { background:#F2F2F2; font-weight:700; font-size:13.5px; }
.tr-tag { display:inline-block; padding:1px 7px; font-size:11px; font-weight:700; border-radius:2px; white-space:nowrap; }
.tr-ok { background:#E8F3E9; color:#2E7D32; }
.tr-warn { background:#FBEFD9; color:#B45309; }
.tr-bad { background:#F9E1E1; color:#C00000; }
.tr-minutes { margin:4px 0 8px 18px; padding:0; }
.tr-minutes li { margin:5px 0; }
.tr-minutes b { color:#C00000; }
.tr-fig { margin:10px 0 4px; overflow-x:auto; }
.tr-fig svg { min-width:1100px; }
@media (max-width:900px) { .tr-status { grid-template-columns:1fr 1fr; } }
`;
  document.head.appendChild(style);
}

function tag(el, kind, label) {
  return el("span", { class: `tr-tag tr-${kind}` }, [label]);
}

function heading(el, title, small) {
  return el("h2", { class: "tr-h" }, [
    title,
    small ? el("small", {}, [small]) : null,
  ]);
}

function table(el, headers, rows, { numeric = [], total = false } = {}) {
  return el("table", { class: "tr-table" }, [
    el("thead", {}, [el("tr", {}, headers.map((h, i) => el("th", { class: numeric.includes(i) ? "num" : "" }, [h])))]),
    el("tbody", {}, rows.map((row, ri) => {
      const isTotal = total && ri === rows.length - 1;
      return el("tr", { class: isTotal ? "total" : "" }, row.map((cell, i) => {
        const cls = [numeric.includes(i) ? "num" : "", i === 0 && !isTotal ? "ref" : ""].filter(Boolean).join(" ");
        return el("td", { class: cls }, [cell]);
      }));
    })),
  ]);
}

function figure(svg, caption) {
  const fig = document.createElement("figure");
  fig.className = "tr-fig";
  fig.innerHTML = svg;
  if (caption) {
    const cap = document.createElement("figcaption");
    cap.className = "tr-note";
    cap.textContent = caption;
    fig.appendChild(cap);
  }
  return fig;
}

function shell(el, tab, body) {
  const root = el("div", { class: "tr-wrap" }, [
    el("div", { class: "tr-tabs" }, [
      el("a", { href: "#/transitions/5034", class: tab === "dashboard" ? "on" : "" }, ["Dashboard"]),
      el("a", { href: "#/transitions/5034/documents", class: tab === "documents" ? "on" : "" }, ["Asset record"]),
    ]),
    ...body,
  ]);
  return root;
}

function renderList(el) {
  return el("div", { class: "tr-wrap" }, [
    el("h1", { class: "tr-h", style: "margin-top:0" }, ["Transitions"]),
    table(el,
      ["MSN", "Aircraft", "Lessee", "Type", "Expiry", "Next milestone", "Next operator"],
      [[
        el("a", { href: "#/transitions/5034" }, [P.msn]),
        `${P.type} · ${P.reg}`,
        P.lessee,
        P.transition,
        P.expiry,
        "Out of service Jan-27 — Lake City FL tbc",
        "To be confirmed",
      ]],
    ),
    el("p", { class: "tr-note" }, ["Delivery to Air Canada on 03-May-24 is a prior event, not a second open project."]),
  ]);
}

function renderDashboard(el) {
  const condRows = P.conditions.map((r) => [r[0], r[1], r[2], tag(el, r[3], r[4]), r[5]]);
  const eolRows = P.eol.map((r) => r.slice());
  eolRows.push(["", "Total estimated EOL adjustment in DAE's favour", "", "", "", "", P.eolTotal, ""]);
  const romRows = P.rom.map((r) => r.slice());
  romRows.push(["", P.romTotalNote, P.romTotal, "", "", ""]);
  return shell(el, "dashboard", [
    el("header", { class: "tr-head" }, [
      el("div", {}, [
        el("h1", {}, ["MSN 5034 — Air Canada redelivery — control sheet"]),
        el("div", { class: "tr-sub" }, [P.header]),
      ]),
      el("div", { class: "tr-stamp" }, [
        `Version ${P.version} — `,
        el("b", {}, [P.versionDate]),
        el("br"),
        P.stamp,
      ]),
    ]),
    el("div", { class: "tr-status" }, P.status.map(([k, v]) => el("div", {}, [
      el("span", {}, [k]),
      el("b", {}, [v]),
    ]))),
    el("div", { class: "tr-rule" }, [el("b", {}, ["Governing rule. "]), P.rule]),
    heading(el, P.minutesTitle, P.minutesNote),
    el("ol", { class: "tr-minutes" }, P.minutes.map(([title, text]) => el("li", {}, [el("b", {}, [title + " "]), text]))),
    heading(el, "Timeline to redelivery", "Lease dates in red; planning dates in grey; ROM lead times counted back from 02-May-27"),
    figure(TIMELINE_SVG, "Launch-by dates assume the mod must be complete at 02-May-27. Any later target delivery moves them right by the same number of days. LH wing refuelling and CPDLC cannot be embodied by 02-May-27 on the quoted lead time."),
    heading(el, "Return conditions — executed Lease, all clauses that touch the redelivery", P.conditionsNote),
    table(el, ["Lease ref", "Condition", "Position at 02-May-27", "Status", "Carried by"], condRows),
    heading(el, "Remaining life at 02-May-27 vs Schedule 9 minimum", "What the next operator inherits, clause by clause"),
    figure(LIFE_SVG, "Months bars to a 120 MO scale; FC bars to a 12,000 FC scale. Sources: Tech Spec 30-Jun-26, ACTracker 30-Jun-26, EOL Project Shape workbook 10-Sep-26."),
    heading(el, "Estimated EOL adjustment — Lease §22.3", P.eolNote),
    table(el, ["Lease ref", "Maintenance event", "Last event", "Next due", "Time used since Delivery (Δ)", "Redelivery condition", "Potential EOL payment (USD)", "Met?"], eolRows, { numeric: [4, 6], total: true }),
    el("p", { class: "tr-note" }, [P.eolFoot]),
    heading(el, "Next operator candidates and flags"),
    table(el, ["Candidate", "Flag / authority", "Document", "Delivery target", "What it needs beyond Schedule 9", "Status"], P.candidates),
    el("p", { class: "tr-note" }, [P.candidatesNote]),
    heading(el, "ROM — MinRes Air (Australia) mods", P.romNote),
    table(el, ["#", "Modification", "ROM (USD)", "Lead time", "Launch-by for 02-May-27", "Remarks"], romRows, { numeric: [2], total: true }),
    heading(el, "Open items register"),
    table(el, ["#", "Item", "Owner", "Due / trigger", "Raised"], P.openItems),
    heading(el, "Change log"),
    table(el, ["Version", "Date", "Change"], P.changelog),
    el("p", { class: "tr-note" }, [P.sources]),
  ]);
}

function llpTable(el, esn, rows) {
  const body = rows.map((r) => {
    const left = Number(String(r[5]).replace(/,/g, ""));
    const atReturn = (left - 1166).toLocaleString("en-US");
    return [r[0], r[1], r[2], r[3], r[4], r[5], atReturn];
  });
  return el("div", {}, [
    heading(el, `ESN ${esn} LLP lines`, "Tech Spec 30-Jun-26. Projected column subtracts 1,166 FC to 02-May-27."),
    table(el, ["Part", "P/N", "Serial", "FC used", "Life limit", "FC left 30-Jun-26", "FC left at 02-May-27"], body, { numeric: [3, 4, 5, 6] }),
  ]);
}

function renderRecord(el) {
  return shell(el, "documents", [
    el("header", { class: "tr-head" }, [
      el("div", {}, [
        el("h1", {}, ["MSN 5034 — asset record"]),
        el("div", { class: "tr-sub" }, [`Read from the Tech Spec dated ${P.assetAsOf}. ${P.utilisation}`]),
      ]),
    ]),
    heading(el, "Major components"),
    table(el, ["Item", "Identity", "TSN", "CSN", "Event", "Source"], P.components),
    el("p", { class: "tr-note" }, [P.airframe.checks]),
    heading(el, "Configuration carried on the Tech Spec"),
    table(el, ["Item", "Value", "Source"], P.config),
    el("p", { class: "tr-note" }, [P.llpNote]),
    llpTable(el, "643877", P.llp["643877"]),
    llpTable(el, "643888", P.llp["643888"]),
    el("p", { class: "tr-note" }, [P.sources]),
  ]);
}

export function renderTransitionsRoute({ el, routeKey }) {
  ensureStyles();
  const key = String(routeKey || "").trim().replace(/\/+$/, "");
  if (key === "transitions") return renderList(el);
  const match = /^transitions\/([^/]+)(?:\/([^/]+))?$/.exec(key);
  if (!match) return renderList(el);
  if (match[1] !== P.id) {
    return el("div", { class: "tr-wrap" }, [
      el("h1", {}, [`No transition for ${match[1]}`]),
      el("a", { href: "#/transitions" }, ["Back to all transitions"]),
    ]);
  }
  if (match[2] === "documents") return renderRecord(el);
  return renderDashboard(el);
}
